import { Badge, Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  CalendarIcon,
  CheckCircle2Icon,
  ClipboardListIcon,
  EditIcon,
  ExternalLinkIcon,
  FileTextIcon,
  GaugeIcon,
  MailIcon,
  MapPinIcon,
  MegaphoneIcon,
  PhoneIcon,
  SendIcon,
  UserRoundIcon,
  UsersRoundIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { formatDateTimeLocal } from "@/shared/lib/date-time-performance";
import { buildPerformanceRequestWhatsAppUrl } from "@/shared/lib/whatsapp";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { WhatsAppLinkButton } from "@/shared/ui/WhatsAppLinkButton";
import {
  isAwaitingConvert,
  isAwaitingConvertStale,
} from "../../domain/services/awaiting-convert";
import {
  useRequestCalendarSyncQuery,
  useRequestQuery,
  useResyncRequestCalendar,
} from "../api/queries";
import { GoogleCalendarSyncCard } from "../ui/GoogleCalendarSyncCard";
import { PriorityOrderBadge } from "../ui/PriorityOrderBadge";

const STATUS_LABELS: Record<string, string> = {
  new: "ໃໝ່",
  negotiating: "ກຳລັງເຈລະຈາ",
  pending_quote: "ລໍຖ້າໃບສະເໜີລາຄາ",
  won: "ຢືນຢັນແລ້ວ",
  lost: "ເສຍໂອກາດ",
  cancelled: "ຍົກເລີກ",
};

const CHANNEL_LABELS: Record<string, string> = {
  phone: "ໂທລະສັບ",
  line: "LINE",
  facebook: "Facebook",
  walk_in: "ເຂົ້າມາຕິດຕໍ່",
  referral: "ແນະນຳ",
  other: "ອື່ນໆ",
};

const STATUS_VARIANTS: Record<
  string,
  "default" | "secondary" | "outline" | "destructive"
> = {
  new: "secondary",
  negotiating: "outline",
  pending_quote: "outline",
  won: "default",
  lost: "secondary",
  cancelled: "destructive",
};

function fmtDate(value: Date | string | null | undefined) {
  if (!value) return "—";
  return formatDateTimeLocal(new Date(value));
}

function requestCode(id: string) {
  return `PR-${id.slice(0, 8).toUpperCase()}`;
}

function DetailLabel({ children }: { children: ReactNode }) {
  return (
    <p className="mb-2 font-semibold text-muted-foreground text-xs uppercase tracking-wide">
      {children}
    </p>
  );
}

function AuditItem({
  icon,
  title,
  time,
  active,
}: {
  icon: ReactNode;
  title: string;
  time: string;
  active?: boolean;
}) {
  return (
    <div className="flex gap-4">
      <div
        className={
          active
            ? "flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground"
            : "flex size-8 shrink-0 items-center justify-center rounded-full border border-border bg-background text-muted-foreground"
        }
      >
        {icon}
      </div>
      <div>
        <p className="font-medium text-sm">{title}</p>
        <p className="mt-1 text-muted-foreground text-xs">{time}</p>
      </div>
    </div>
  );
}

export function RequestDetailPage() {
  const { id } = useParams({ from: "/app/performance/requests/$id" });
  const nav = useNavigate({ from: "/app/performance/requests/$id" });
  const { data: request, isLoading } = useRequestQuery(id);
  const calendarSync = useRequestCalendarSyncQuery(id);
  const resyncCalendar = useResyncRequestCalendar(id);
  const canEdit = useActionPermission(["performance-requests:update"]);
  const canCreateEvent = useActionPermission(["performance-events:create"]);

  if (isLoading) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ຄິວຄຳຂໍ", to: "/app/performance/requests" },
              { label: "..." },
            ]}
          />
        </Header>
        <Main>
          <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
        </Main>
      </>
    );
  }

  if (!request) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ຄິວຄຳຂໍ", to: "/app/performance/requests" },
              { label: "ບໍ່ພົບຂໍ້ມູນ" },
            ]}
          />
        </Header>
        <Main>
          <p className="text-destructive">ບໍ່ພົບຄຳຂໍ</p>
        </Main>
      </>
    );
  }

  const canConvert = canEdit && canCreateEvent && !request.convertedEventId;
  const statusLabel = STATUS_LABELS[request.status] ?? request.status;
  const channelLabel = CHANNEL_LABELS[request.channel] ?? request.channel;
  const awaitingConvert = isAwaitingConvert(request);
  const awaitingStale = isAwaitingConvertStale(request);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຄິວຄຳຂໍ", to: "/app/performance/requests" },
            { label: request.title },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-bold text-3xl tracking-tight">
                {request.title}
              </h1>
              <Badge
                variant={STATUS_VARIANTS[request.status] ?? "secondary"}
                className="mt-0.5"
              >
                {statusLabel}
              </Badge>
              {awaitingConvert ? (
                <Badge
                  className={`mt-0.5 border-transparent ${
                    awaitingStale
                      ? "bg-amber-500 text-white hover:bg-amber-500"
                      : "bg-amber-100 text-amber-900 hover:bg-amber-100 dark:bg-amber-900/40 dark:text-amber-200"
                  }`}
                >
                  ລໍຖ້າແປງ
                </Badge>
              ) : null}
              {request.convertedEventWasPostponed ||
              request.convertedEventDateTbd ? (
                <Badge className="mt-0.5 border-transparent bg-amber-500 text-white hover:bg-amber-500">
                  {request.convertedEventDateTbd ? "ຍັງບໍ່ກຳນົດ" : "ເລື່ອນ"}
                </Badge>
              ) : null}
              <PriorityOrderBadge
                priorityOrder={request.priorityOrder}
                className="mt-0.5"
              />
            </div>
            <p className="mt-1.5 text-muted-foreground text-sm">
              ຄຳຂໍ {requestCode(request.id)} · ຮັບເມື່ອ {fmtDate(request.createdAt)}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/performance/requests" })}
            >
              <ArrowLeftIcon className="size-4" />
              ກັບຄືນ
            </Button>
            {request.convertedEventId ? (
              <Button
                onClick={() =>
                  nav({
                    to: "/app/performance/events/$id",
                    params: { id: request.convertedEventId ?? "" },
                  })
                }
              >
                <ExternalLinkIcon className="size-4" />
                ເປີດງານສະແດງ
              </Button>
            ) : (
              canConvert && (
                <Button
                  onClick={() =>
                    nav({
                      to: "/app/performance/requests/$id/convert",
                      params: { id: request.id },
                    })
                  }
                >
                  <SendIcon className="size-4" />
                  ສ້າງງານສະແດງ
                </Button>
              )
            )}
          </div>
        </div>

        <GoogleCalendarSyncCard
          status={calendarSync.data}
          canSync={canEdit}
          isSyncing={resyncCalendar.isPending}
          onSync={() => resyncCalendar.mutate()}
        />

        <div className="mb-6 grid grid-cols-1 divide-y rounded-xl border bg-card md:grid-cols-3 md:divide-x md:divide-y-0">
          <div className="p-5">
            <div className="mb-4 flex items-center gap-2">
              <MegaphoneIcon className="size-4 text-primary" />
              <span className="font-semibold text-xs uppercase tracking-widest">
                ຊ່ອງທາງ
              </span>
            </div>
            <p className="font-bold text-lg">{channelLabel}</p>
          </div>
          <div className="p-5">
            <div className="mb-4 flex items-center gap-2">
              <CalendarIcon className="size-4 text-primary" />
              <span className="font-semibold text-xs uppercase tracking-widest">
                ວັນທີຕ້ອງການ
              </span>
            </div>
            <p className="font-bold text-lg">
              {request.dateMode === "show_dates" &&
              (request.showDates?.length ?? 0) > 0
                ? `${request.showDates!.length} ວັນໂຊ`
                : fmtDate(request.requestedEventDate)}
            </p>
            {request.dateMode === "show_dates" &&
            (request.showDates?.length ?? 0) > 0 ? (
              <ul className="mt-2 space-y-1 text-muted-foreground text-sm">
                {request.showDates!.map((row) => (
                  <li key={row.id}>{fmtDate(row.showDate)}</li>
                ))}
              </ul>
            ) : null}
          </div>
          <div className="p-5">
            <div className="mb-4 flex items-center gap-2">
              <GaugeIcon className="size-4 text-primary" />
              <span className="font-semibold text-xs uppercase tracking-widest">
                ການແປງເປັນງານ
              </span>
            </div>
            <p className="font-bold text-lg">
              {request.convertedEventId ? "ແປງແລ້ວ" : "ລໍຖ້າ"}
            </p>
          </div>
        </div>

        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-6">
            <div className="rounded-xl border bg-card">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
                <div className="flex items-center gap-2">
                  <UserRoundIcon className="size-4 text-primary" />
                  <span className="font-semibold text-sm uppercase tracking-widest">
                    ຜູ້ຕິດຕໍ່ ແລະ ຂໍ້ມູນລູກຄ້າ
                  </span>
                </div>
              </div>
              <div className="p-5">
                <div className="grid gap-6 lg:grid-cols-2">
                  <div>
                    <DetailLabel>ລູກຄ້າ</DetailLabel>
                    <p className="mb-4 font-semibold text-base">
                      {request.customerName || "—"}
                    </p>
                    <DetailLabel>ຜູ້ປະສານງານ</DetailLabel>
                    <div className="flex min-h-18 items-center gap-4 rounded-lg border bg-muted/30 px-4 py-3">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted font-semibold text-muted-foreground text-sm">
                        {request.contactName.slice(0, 1)}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-base">
                          {request.contactName}
                        </p>
                        <p className="truncate text-muted-foreground text-sm">
                          {request.title}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                      <div>
                        <DetailLabel>ອີເມວ</DetailLabel>
                        <p className="flex items-start gap-2 break-all text-sm">
                          <MailIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                          {request.contactEmail ?? "—"}
                        </p>
                      </div>
                      <div>
                        <DetailLabel>ເບີໂທ</DetailLabel>
                        <div className="mt-0.5 flex flex-wrap items-center gap-2">
                          <p className="flex items-center gap-2 text-sm">
                            <PhoneIcon className="size-4 shrink-0 text-muted-foreground" />
                            {request.contactPhone ?? "—"}
                          </p>
                          {request.contactPhone ? (
                            <WhatsAppLinkButton
                              phone={request.contactPhone}
                              href={buildPerformanceRequestWhatsAppUrl({
                                phone: request.contactPhone,
                                contactName: request.contactName,
                                requestTitle: request.title,
                              })}
                              label="ຕິດຕໍ່ WhatsApp"
                            />
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <DetailLabel>ທີ່ຢູ່ລູກຄ້າ</DetailLabel>
                      <p className="flex items-start gap-2 whitespace-pre-wrap text-sm">
                        <MapPinIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                        {request.contactAddress ?? "—"}
                      </p>
                    </div>
                  </div>
                  <div className="rounded-lg border border-l-4 border-l-primary bg-muted/20 px-4 py-4">
                    <DetailLabel>ຂໍ້ມູນບັນຊີລູກຄ້າ</DetailLabel>
                    <p className="font-semibold text-base">
                      {request.customerName || request.contactName}
                    </p>
                    {request.customerName ? (
                      <p className="mt-1 text-muted-foreground text-sm">
                        {request.contactName}
                      </p>
                    ) : null}
                    <p className="mt-2 text-muted-foreground text-sm">
                      ລະຫັດຄຳຂໍ: {requestCode(request.id)}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Badge variant="secondary">{channelLabel}</Badge>
                      <Badge variant="outline">{statusLabel}</Badge>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-xl border bg-card">
              <div className="border-b px-5 py-4">
                <div className="flex items-center gap-2">
                  <FileTextIcon className="size-4 text-primary" />
                  <span className="font-semibold text-sm uppercase tracking-widest">
                    ໝາຍເຫດລາຍລະອຽດ
                  </span>
                </div>
              </div>
              <div className="p-5">
                {request.notes ? (
                  <p className="whitespace-pre-wrap text-sm leading-relaxed">
                    {request.notes}
                  </p>
                ) : (
                  <p className="text-muted-foreground text-sm italic">
                    ຍັງບໍ່ມີໝາຍເຫດ
                  </p>
                )}
              </div>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="rounded-xl border bg-card">
              <div className="border-b px-5 py-4">
                <span className="font-semibold text-sm uppercase tracking-widest">
                  ການມອບໝາຍ
                </span>
              </div>
              <div className="space-y-4 p-5">
                <div>
                  <DetailLabel>ຜູ້ຮັບຜິດຊອບ</DetailLabel>
                  <div className="flex h-12 items-center justify-between rounded-lg border bg-muted/30 px-3">
                    <span className="text-sm">
                      {request.assignedToName ??
                        request.assignedTo ??
                        "ຍັງບໍ່ໄດ້ມອບໝາຍ"}
                    </span>
                    <UserRoundIcon className="size-4 text-muted-foreground" />
                  </div>
                </div>
                <div>
                  <DetailLabel>ທີມກວດສອບ</DetailLabel>
                  <div className="flex items-center gap-2">
                    <div className="flex size-9 items-center justify-center rounded-full border bg-background text-primary">
                      <UsersRoundIcon className="size-4" />
                    </div>
                    <span className="text-muted-foreground text-sm">
                      ທີມປະຕິບັດການ
                    </span>
                  </div>
                </div>
                {canEdit && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() =>
                      nav({
                        to: "/app/performance/requests/$id/edit",
                        params: { id },
                      })
                    }
                  >
                    <EditIcon className="size-4" />
                    ອັບເດດການມອບໝາຍ
                  </Button>
                )}
              </div>
            </div>

            <div className="rounded-xl border bg-card">
              <div className="border-b px-5 py-4">
                <span className="font-semibold text-sm uppercase tracking-widest">
                  ປະຫວັດກິດຈະກຳ
                </span>
              </div>
              <div className="space-y-6 p-5">
                <AuditItem
                  icon={<FileTextIcon className="size-4" />}
                  title="ບັນທຶກຄຳຂໍຖືກອັບເດດ"
                  time={fmtDate(request.updatedAt)}
                />
                <AuditItem
                  icon={<ClipboardListIcon className="size-4" />}
                  title={`ສະຖານະ: ${statusLabel}`}
                  time={fmtDate(request.updatedAt)}
                />
                <AuditItem
                  active
                  icon={<CheckCircle2Icon className="size-4" />}
                  title={
                    request.convertedEventId
                      ? "ແປງເປັນງານສະແດງແລ້ວ"
                      : "ຮັບຄຳຂໍເຂົ້າລະບົບ"
                  }
                  time={fmtDate(request.convertedAt ?? request.createdAt)}
                />
              </div>
            </div>
          </aside>
        </div>
      </Main>
    </>
  );
}
